import { CircularProgress } from '@mui/material';
import { useTheme } from '@mui/styles';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { useCreateTicketFileAccessLinkDataWrapper } from 'src/app/components/data-wrappers/tickets/CreateTicketFileAccessLinkDataWrapper';
import { useFetchTicketByIdDataWrapper } from 'src/app/components/data-wrappers/tickets/FetchTicketByIdDataWrapper';
import { useUploadTicketFileDataWrapper } from 'src/app/components/data-wrappers/tickets/UploadTicketFileDataWrapper';
import { TicketFilesForm } from 'src/app/components/forms/tickets/upload-files-form/TicketFilesForm';
import { useDeleteTicketsFiles } from 'src/app/hooks/tickets/use-delete-tickets-files';
import { FETCH_EVENT_TICKETS_QUERY_KEY } from 'src/app/hooks/tickets/useFetchEventTickets';
import { ErrorReturn } from 'src/app/hooks/useApiFetch';
import useTicketFileAlreadyExistsToast from 'src/app/hooks/useTicketFileAlreadyExistsToast';
import Toaster from 'src/app/utilities/helpers/Toaster';
import { showMappedBackendErrors } from 'src/app/utilities/helpers/errors';
import isTicketAlreadyLinkedError from 'src/data/api/type-guards/isTicketAlreadyLinkedError';
import TicketFile from 'src/data/models/common/ticketFile';
import ErrorsList from 'src/view/components/errors-list/ErrorsList';
import FormButtons from 'src/view/components/form/FormButtons';
import Modal, { ModalBody, ModalFooter } from 'src/view/components/modal/Modal';

interface TicketUploadFilesFormFeatureProps {
    ticketId: string;
    defaultFiles?: TicketFile[];
    eventId: string;
}

export const TicketUploadFilesFormFeature = ({
    ticketId,
    defaultFiles,
}: TicketUploadFilesFormFeatureProps) => {
    const theme = useTheme();

    const queryClient = useQueryClient();

    const showFileAlreadyExistsToast = useTicketFileAlreadyExistsToast();

    const { enqueueSnackbar } = useSnackbar();

    const { fetchTicketById, data: ticketData } = useFetchTicketByIdDataWrapper({});

    const { deleteTicketsFiles } = useDeleteTicketsFiles();

    const { addTicketFile } = useUploadTicketFileDataWrapper({
        disableDefaultSuccessMessage: true,
        useDefaultErrorHandler: false,
    });

    const { createTicketFileAccessLink } = useCreateTicketFileAccessLinkDataWrapper({
        onSuccess: (data) => data,
        onError: () =>
            Toaster.toast('An error occurred while trying to open the ticket file.', {
                variant: 'error',
            }),
    });

    const openTicketFileAccessLink = async (ticketFileId: string): Promise<void> => {
        let response = await createTicketFileAccessLink(ticketFileId);
        window.open(response?.data.url, '_blank');
    };

    const copyTicketFileAccessLink = async (ticketFileId: string): Promise<void> => {
        let response = await createTicketFileAccessLink(ticketFileId);
        await navigator.clipboard.writeText(response?.data.url);
        Toaster.toast('Ticket link copied to clipboard.', { variant: 'info' });
    };

    const [showDeleteAllmodal, setShowDeleteAllModal] = useState(false);
    const [deleteAllFilesIsLoading, setDeleteAllFilesIsLoading] = useState(false);

    const files = ticketData?.data.files || defaultFiles;

    const handleUploadFiles = async (fileUploads: File[]) => {
        if (!fileUploads.length) {
            Toaster.toast('Something went wrong while handling the chosen file(s).', {
                variant: 'error',
            });

            return;
        }

        Toaster.toast('Uploading files...', { variant: 'info' });

        for (const file of fileUploads) {
            try {
                await addTicketFile(ticketId, file, null);

                Toaster.toast(`Successfully attached ${file.name}`, { variant: 'success' });

                fetchTicketById(ticketId);

                // TODO: When uploadTicketFile is moved to React-Query, invalidate below query there
                queryClient.invalidateQueries({
                    queryKey: [FETCH_EVENT_TICKETS_QUERY_KEY],
                    exact: false,
                });
            } catch (e) {
                const error = e as ErrorReturn;

                if (isTicketAlreadyLinkedError(error.parsedErrors[0])) {
                    const { title, eventTicketFiles } = error.parsedErrors[0];

                    showFileAlreadyExistsToast({
                        title,
                        eventId: eventTicketFiles[0].eventId,
                        ticketId: eventTicketFiles[0].ticketId,
                    });
                } else {
                    enqueueSnackbar(<ErrorsList errors={error.parsedErrors} plainStyle />, {
                        variant: 'error',
                    });
                }
            }
        }
    };

    const handleTicketLinkSaved = async (ticketLink: string) => {
        if (!ticketLink) {
            return;
        }

        try {
            await addTicketFile(ticketId, null, ticketLink);

            Toaster.toast('Successfully attached ticket link', { variant: 'success' });

            fetchTicketById(ticketId);

            queryClient.invalidateQueries({
                queryKey: [FETCH_EVENT_TICKETS_QUERY_KEY],
                exact: false,
            });
        } catch (e) {
            const error = e as ErrorReturn;

            if (isTicketAlreadyLinkedError(error.parsedErrors[0])) {
                const { title, eventTicketFiles } = error.parsedErrors[0];

                showFileAlreadyExistsToast({
                    title,
                    eventId: eventTicketFiles[0].eventId,
                    ticketId: eventTicketFiles[0].ticketId,
                });
            } else {
                throw e;
            }
        }
    };

    const deleteAllTickets = async () => {
        if (!files) return;

        setDeleteAllFilesIsLoading(true);

        try {
            deleteTicketsFiles([ticketId]);
            setDeleteAllFilesIsLoading(false);
            await fetchTicketById(ticketId);
            setShowDeleteAllModal(false);
        } catch (e) {
            showMappedBackendErrors(e);
        }
    };

    const renderDeleteAllTicketsModal = () => (
        <Modal
            open={showDeleteAllmodal}
            title="Confirm your action"
            onClose={() => setShowDeleteAllModal(false)}
        >
            <>
                <ModalBody>
                    You are about to delete <strong>{ticketData?.data.files.length}</strong> files.
                    Are you sure you want to continue?
                </ModalBody>
                <ModalFooter>
                    <FormButtons
                        buttons={[
                            {
                                children: 'Delete selected files',
                                disabled: deleteAllFilesIsLoading,
                                startIcon: deleteAllFilesIsLoading && (
                                    <CircularProgress size={theme.layout.loader.sizes.small} />
                                ),
                                onClick: deleteAllTickets,
                            },
                        ]}
                    />
                </ModalFooter>
            </>
        </Modal>
    );

    return (
        <>
            <TicketFilesForm
                dropzoneProps={{
                    onDrop: (acceptedFiles) => handleUploadFiles(acceptedFiles),
                }}
                onUpload={(files: File[]) => handleUploadFiles(files)}
                ticketFiles={ticketData?.data.files || defaultFiles}
                ticketUploadedFilesTableProps={{
                    onFileDeleted: () => fetchTicketById(ticketId),
                    onClickTicketFile: openTicketFileAccessLink,
                    onClickCopyTicketLink: copyTicketFileAccessLink,
                    loading: false,
                    ticketId,
                }}
                onDeleteAll={() => setShowDeleteAllModal(true)}
                onTicketLinkSaved={async (values) => {
                    if (values.ticketLink) {
                        await handleTicketLinkSaved(values.ticketLink);
                    }
                }}
            />
            {renderDeleteAllTicketsModal()}
        </>
    );
};
